// Sentry must be imported before all other modules
import './instrument';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './index.css';

// Initialize DAL
import { initDalClient, initClient, loadConfigFromEnv, getClient } from '@littlebooker/dal';
import { initEmailService } from '@littlebooker/dal';
import type { DalConfigWithAdmin } from '@littlebooker/dal';
import { getAccessToken } from './contexts/auth/accessToken';

type GlobalWithProcess = typeof globalThis & { process?: NodeJS.Process };

const ensureProcessEnv = () => {
  // Get env vars from import.meta.env (Vite exposes VITE_ prefixed vars)
  const envFromImportMeta = Object.entries(import.meta.env).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      if (typeof value === 'string') {
        acc[key] = value;
      } else if (typeof value === 'boolean') {
        acc[key] = String(value);
      }
      return acc;
    },
    {},
  );

  // Also check for SUPABASE_ and POSTGREST_ vars that might be loaded via vite.config
  // Vite's loadEnv might load them but they won't be in import.meta.env without VITE_ prefix
  // So we check if they're available via the window object or other means
  const additionalEnv: Record<string, string> = {};
  
  // Try to get from window.__ENV__ if vite config exposes them
  if (typeof window !== 'undefined' && (window as any).__ENV__) {
    Object.assign(additionalEnv, (window as any).__ENV__);
  }

  const globalScope = globalThis as GlobalWithProcess;

  if (!globalScope.process) {
    globalScope.process = {
      env: { ...envFromImportMeta, ...additionalEnv },
    } as unknown as NodeJS.Process;
    return;
  }

  globalScope.process.env = {
    ...(globalScope.process.env ?? {}),
    ...envFromImportMeta,
    ...additionalEnv,
  };
};

// Polyfill `process.env` for packages expecting the Node global
ensureProcessEnv();

// Load and initialize DAL
try {
  // In Vite production builds, environment variables must be prefixed with VITE_
  // Read directly from import.meta.env instead of using loadConfigFromEnv()
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  const schema = (import.meta.env.VITE_SUPABASE_SCHEMA as string) || 'littlebooker';
  
  if (!supabaseUrl || !supabaseAnonKey) {
    console.error('❌ DAL config missing required fields:', { 
      hasUrl: !!supabaseUrl, 
      hasKey: !!supabaseAnonKey,
      env: import.meta.env 
    });
    throw new Error('Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
  }

  // Ensure URL includes /rest/v1 for Supabase
  const postgrestUrl = supabaseUrl.includes('/rest/v1') 
    ? supabaseUrl 
    : `${supabaseUrl.replace(/\/$/, '')}/rest/v1`;
  
  const apiBaseUrl = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:3001';

  const newConfig: DalConfigWithAdmin = {
    url: postgrestUrl,
    apiKey: supabaseAnonKey,
    schema: schema,
    supabaseUrl: supabaseUrl,
    supabaseAnonKey: supabaseAnonKey,
    storageProvider: 'r2',
    apiBaseUrl,
    // In-memory access token, kept fresh by AuthContext's refresh scheduler.
    getAuthToken: getAccessToken,
  };

  // Initialize the new client system (used by tasks.ts and task-reminders.ts)
  const newClient = initDalClient(newConfig);
  console.log('✅ New DAL client initialized:', { hasClient: !!newClient, url: newConfig.url.substring(0, 50) + '...' });

  // Verify the client is accessible
  try {
    const testClient = getClient();
    console.log('✅ Verified new DAL client is accessible:', { hasClient: !!testClient });
  } catch (verifyError) {
    console.error('❌ Failed to verify DAL client:', verifyError);
    throw verifyError; // Re-throw if client verification fails
  }

  // Initialize the old client system (used by index.ts functions like createTask/updateTask)
  const oldClientConfig = initClient(newConfig);
  console.log('✅ Old DAL client initialized:', { hasConfig: !!oldClientConfig });
  
  console.log('✅ DAL initialized (both client systems):', {
    newSystem: { hasUrl: !!newConfig.url, hasKey: !!newConfig.apiKey },
    oldSystem: { hasUrl: !!newConfig.url, hasKey: !!newConfig.apiKey },
  });

  // Initialize email service (if Resend credentials are available)
  const globalScope = globalThis as GlobalWithProcess;
  const resendKey = globalScope.process?.env?.VITE_RESEND_API_KEY as string | undefined;
  const resendFrom = globalScope.process?.env?.VITE_RESEND_FROM_EMAIL as string | undefined;
  const resendFromName = (globalScope.process?.env?.VITE_RESEND_FROM_NAME || 'LittleBooker') as string;
  const adminBaseUrl = (globalScope.process?.env?.VITE_ADMIN_BASE_URL || 'http://localhost:5173') as string;

  if (resendKey && resendFrom) {
    try {
      initEmailService({
        apiKey: resendKey,
        fromEmail: resendFrom,
        fromName: resendFromName,
        baseUrl: adminBaseUrl,
      });
      console.log('✅ Email service initialized');
    } catch (emailError) {
      console.error('❌ Failed to initialize email service:', emailError);
      // Don't throw - app can still function without email
    }
  } else {
    console.warn('⚠️  Email service not initialized: RESEND_API_KEY or RESEND_FROM_EMAIL not found');
  }
} catch (error) {
  console.error('❌ Failed to initialize DAL:', error);
  // Show error to user in console - app will still try to start but operations may fail
  if (typeof window !== 'undefined') {
    console.error('The app may not function correctly. Please check your environment variables.');
  }
}

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - prevents constant refetching
      gcTime: 10 * 60 * 1000, // 10 minutes - keep unused data in cache longer
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
